<template>
<nav id="site-navigation" class="has-branding main-navigation nav-align-right sub-menu-right" aria-label="Primary" itemtype="https://schema.org/SiteNavigationElement" itemscope>
  <div class="inside-navigation">
    <div class="navigation-branding">
      <div class="site-logo">
        <router-link :to="homePath" :title="siteName" rel="home">
          <img class="header-image is-logo-image" :alt="siteName" :src="logo" :title="siteName">
        </router-link>
      </div>
      <p class="main-title" itemprop="headline">
        <router-link :to="homePath" rel="home">{{ siteName }}</router-link>
      </p>
    </div>

    <ul v-if="isMenuClicked" id="menu-primary-menu-mobile" class="menu sf-menu mobile-menu-page">
      <li v-for="item in navItems" :key="'m-' + item.to" class="menu-item">
        <router-link :to="item.to" :class="{ 'download-btn': item.highlight }">{{ item.label }}</router-link>
      </li>
      <li class="menu-item menu-item-lang">
        <LanguageSwitch :locale="locale" />
      </li>
    </ul>

    <div id="primary-menu" class="main-nav">
      <div class="mobile-menu" v-if="$global.isMobileDevice()">
        <LanguageSwitch :locale="locale" class="mobile-lang" />
        <div class="menu-toggle" @click="isMenuClicked = !isMenuClicked">
          <img src="@/assets/menu.svg" alt="menu button" v-if="!isMenuClicked">
          <img src="@/assets/xmark-black.svg" alt="close button" v-else>
        </div>
      </div>
      <ul v-else id="menu-primary-menu" class="menu sf-menu">
        <li v-for="item in navItems" :key="item.to" class="menu-item" :class="{ 'header-download': item.highlight }">
          <router-link :to="item.to" :class="{ 'download-btn': item.highlight }">{{ item.label }}</router-link>
        </li>
        <li class="menu-item menu-item-lang">
          <LanguageSwitch :locale="locale" />
        </li>
      </ul>
    </div>
  </div>
</nav>
</template>

<script>
import siteConfig from '@/config/site';
import LanguageSwitch from './LanguageSwitch.vue';

export default {
  name: 'HeadNav',
  components: {
    LanguageSwitch,
  },
  props: {
    locale: {
      type: String,
      default: 'id',
    },
  },
  data() {
    return {
      isMenuClicked: false,
      siteName: siteConfig.siteName,
      logo: siteConfig.logo,
    };
  },
  computed: {
    homePath() {
      return this.locale === 'en' ? '/en/' : '/';
    },
    navItems() {
      return siteConfig.nav.map((item) => {
        if (item.homeAware) {
          return { ...item, to: this.homePath };
        }
        return item;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
#site-navigation {
  background: #fff;
  border-bottom: 1px solid #eaeaea;
  box-shadow: none;

  .inside-navigation {
    width: min(92%, 1200px);
    min-height: 60px;
    margin: 0 auto;
    z-index: 9999;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap !important;
    gap: 12px;
  }

  .main-nav > ul {
    flex-wrap: nowrap !important;
  }
}

.navigation-branding {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  min-width: 0;

  .site-logo img {
    display: block;
    width: auto;
    height: 60px;
    max-height: 60px;
    border-radius: 0;
    object-fit: contain;
  }

  .main-title {
    margin: 0;
    font-size: clamp(18px, 2.2vw, 24px);
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: 0;
    white-space: nowrap;

    a {
      color: #222;
      text-decoration: none;

      &:hover {
        color: #0089ae;
      }
    }
  }
}

.main-nav {
  flex-shrink: 1;
  min-width: 0;

  .menu {
    list-style-type: none !important;
    margin: 0 !important;
    padding: 0;
    display: flex;
    align-items: center;
    flex-wrap: nowrap !important;
    gap: 0;
    white-space: nowrap;

    .menu-item {
      flex-shrink: 0;
      list-style: none !important;
      list-style-type: none !important;
      margin-bottom: 0 !important;
      padding-left: 0 !important;
    }

    .menu-item::marker {
      content: none;
    }

    li a {
      display: inline-flex;
      align-items: center;
      min-height: 60px;
      padding: 0 14px;
      background: transparent !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      font-size: 15px;
      line-height: 60px;
      font-weight: 400 !important;
      text-decoration: none;
      color: #303030 !important;
      transition: color 0.2s ease;
      white-space: nowrap;

      &:hover {
        color: #0089ae !important;
        text-decoration: none;
      }
    }

    .header-download {
      margin-top: 0 !important;

      a {
        margin: 0 !important;
        padding: 0 14px !important;
        line-height: 60px !important;
      }
    }
  }
}

.menu-item-lang {
  display: flex;
  align-items: center;
  flex-shrink: 0;

  :deep(.lang-switch) {
    margin-left: 8px;
    padding: 2px;
    font-size: 14px;
  }
}

@media screen and (max-width: 1100px) {
  .main-nav .menu li a {
    padding: 0 10px;
    font-size: 14px;
  }

  .menu-item-lang :deep(.lang-switch) {
    margin-left: 6px;
    padding: 2px;
    font-size: 13px;
  }
}

@media screen and (max-width: 768px) {
  #site-navigation {
    .inside-navigation {
      width: calc(100% - 28px);
      min-height: 56px;
    }
  }

  .navigation-branding {
    gap: 8px;

    .site-logo img {
      height: 40px;
      max-height: 40px;
    }

    .main-title {
      font-size: 18px;
    }
  }

  .inside-navigation {
    .mobile-menu-page {
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      z-index: 1001;
      margin: 0 !important;
      padding: 0;
      list-style: none;

      .menu-item {
        list-style: none !important;
        list-style-type: none !important;
        padding: 0;
        margin-bottom: 0 !important;
        background: #fff;
        border-bottom: 1px solid #f0f0f0;

        a {
          min-height: auto;
          padding: 14px 20px;
          line-height: 1.4;
          font-size: 15px;
          text-align: center;
          color: #303030 !important;
          display: block;
        }
      }
    }
  }

  .main-nav {
    .mobile-menu {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;

      .menu-toggle {
        display: flex;
        justify-content: center;
        padding: 10px;

        img {
          width: 22px;
          height: 22px;
        }
      }
    }
  }
}
</style>
